// H2HHubPage.js — /head-to-head: the way into the head-to-head pages. Pick
// any two clubs; the famous derbies and rivalries with their records; this
// season's 20 clubs as a grid of meeting counts (every square a link); and
// the most-played fixtures. Data: ?pairs=1 (every pair that has met) and
// ?teams=1 (crests, and which clubs are in this season).

import React, { useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Autocomplete, Box, Button, Skeleton, TextField, Typography } from "@mui/material";
import SEO from "./components/SEO";
import AdSlot from "./components/AdSlot";
import { CtaRow, H2, P, PageHeader, ProsePage } from "./components/Prose";
import { BRAND } from "./theme";
import { formatDate } from "./components/heatmap/utils";
import { useSeededQuery } from "./components/scorelines/data";
import { darkField } from "./components/clubs/LeagueControls";
import { clubInfo, seasonIdStart } from "./components/clubs/data";
import { DERBIES, h2hPath, teamForSlug } from "./components/clubs/h2h";

const SITE_URL = "https://premierleaguescorigami.com";
const sortName = (team) => clubInfo(team).name.replace(/^AFC /, "");

export default function H2HHubPage() {
  const pairsResp = useSeededQuery("pairs=1");
  const teamsResp = useSeededQuery("teams=1");
  const ready = pairsResp.data && teamsResp.data;
  return (
    <ProsePage wide>
      <SEO
        title="Premier League head to head records for every fixture"
        description="Every Premier League head to head since 1992/93: pick two clubs for every meeting, the record, biggest wins and the scoreline grid, from derbies to one-offs."
        path="/head-to-head"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Head to head", item: `${SITE_URL}/head-to-head` },
          ],
        }}
      />
      {pairsResp.error || teamsResp.error ? (
        <>
          <PageHeader eyebrow="Head to head" title="Premier League head to head" />
          <P>The head-to-head records couldn't be loaded right now. Please try again shortly.</P>
        </>
      ) : !ready ? (
        <>
          <PageHeader eyebrow="Head to head" title="Premier League head to head" />
          <Skeleton variant="rounded" height={56} sx={{ bgcolor: "rgba(255,255,255,0.06)", borderRadius: 2, mb: 3, maxWidth: 820 }} />
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 1.25 }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={110} sx={{ bgcolor: "rgba(255,255,255,0.06)", borderRadius: 2 }} />
            ))}
          </Box>
        </>
      ) : (
        <Hub pairs={pairsResp.data.items} teams={teamsResp.data.items} />
      )}
      <CtaRow
        links={[
          { to: "/clubs", label: "Every club", primary: true },
          { to: "/seasons", label: "Every season" },
          { to: "/all-time-table", label: "All-time table" },
        ]}
      />
    </ProsePage>
  );
}

function Hub({ pairs, teams }) {
  // pairs rows: [teamA, teamB, meetings, aWins, draws, bWins, lastDate], names alphabetical.
  const byKey = useMemo(() => new Map(pairs.map((p) => [`${p[0]}|${p[1]}`, p])), [pairs]);
  const pairOf = (x, y) => byKey.get(x < y ? `${x}|${y}` : `${y}|${x}`);
  const crest = useMemo(() => new Map(teams.map((t) => [t.team, t.image])), [teams]);
  const current = useMemo(() => {
    const latest = Math.max(...teams.map((t) => seasonIdStart(t.lastSeasonId)));
    return teams.filter((t) => seasonIdStart(t.lastSeasonId) === latest).map((t) => t.team).sort((x, y) => sortName(x).localeCompare(sortName(y)));
  }, [teams]);
  const top = pairs[0];
  const name = (team) => clubInfo(team).name;
  const lede =
    `${pairs.length} pairs of clubs have met in the Premier League since 1992/93. ` +
    `The most-played fixtures have met ${top[2]} times. Pick any two clubs for every meeting, the record, the biggest wins and their scoreline grid, or start with a derby.`;

  return (
    <>
      <Box sx={{ maxWidth: 820 }}>
        <PageHeader eyebrow="Head to head" title="Premier League head to head" lede={lede} />
      </Box>
      <Picker teams={teams} crest={crest} />

      <H2 id="derbies">Derbies and rivalries</H2>
      <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, mb: 2, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(auto-fill, minmax(250px, 1fr))" }, gap: 1.25 }}>
        {DERBIES.map(([sa, sb, label]) => {
          const x = teamForSlug(sa);
          const y = teamForSlug(sb);
          const p = x && y && pairOf(x, y);
          if (!p) return null;
          return (
            <li key={`${sa}-${sb}`}>
              <PairCard p={p} label={label} crest={crest} />
            </li>
          );
        })}
      </Box>

      <H2 id="this-season">This season's clubs</H2>
      <P>How often each pair of this season's clubs has met in the Premier League. Select a square for that head to head.</P>
      <Matrix clubs={current} pairOf={pairOf} crest={crest} />

      <AdSlot position="records" />

      <H2 id="most-played">Most-played fixtures</H2>
      <Box sx={{ overflowX: "auto", mb: 2 }}>
        <Box
          component="table"
          sx={{
            width: "100%",
            minWidth: 560,
            borderCollapse: "collapse",
            fontFeatureSettings: '"tnum"',
            "& th": { textAlign: "right", color: BRAND.textSecondary, fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", py: 1, px: 1, borderBottom: "1px solid rgba(255,255,255,0.12)" },
            "& td": { textAlign: "right", py: 1, px: 1, borderBottom: "1px solid rgba(255,255,255,0.06)", color: "#d7dae3", fontSize: 14.5, whiteSpace: "nowrap" },
            "& th:first-of-type, & td:first-of-type": { textAlign: "left", pl: 0 },
            "& td a": { color: "#fff", fontWeight: 700, textDecoration: "none" },
            "& td a:hover": { color: BRAND.cyan },
          }}
        >
          <thead>
            <tr>
              <th>Fixture</th>
              <th>Meetings</th>
              <th>Record</th>
              <th>Last met</th>
            </tr>
          </thead>
          <tbody>
            {pairs.slice(0, 20).map((p) => (
              <tr key={`${p[0]}|${p[1]}`}>
                <td>
                  <RouterLink to={h2hPath(p[0], p[1])}>
                    {name(p[0])} v {name(p[1])}
                  </RouterLink>
                </td>
                <td>{p[2]}</td>
                <td>
                  {p[3]}–{p[4]}–{p[5]}
                </td>
                <td>{formatDate(`${p[6]}T00:00:00Z`)}</td>
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>
      <Typography sx={{ color: BRAND.textSecondary, fontSize: 13, maxWidth: 820 }}>
        Record: wins for the first-named club, draws, wins for the second.
      </Typography>
    </>
  );
}

function Picker({ teams, crest }) {
  const navigate = useNavigate();
  const options = useMemo(() => teams.map((t) => t.team).sort((x, y) => sortName(x).localeCompare(sortName(y))), [teams]);
  const [x, setX] = useState(null);
  const [y, setY] = useState(null);
  const field = (id, label, value, onChange, other) => (
    <Autocomplete
      id={id}
      options={options}
      value={value}
      onChange={(_, v) => onChange(v)}
      getOptionLabel={(team) => clubInfo(team).name}
      getOptionDisabled={(team) => team === other}
      renderOption={(props, team) => {
        const { key, ...rest } = props;
        return (
          <li key={key} {...rest}>
            {crest.get(team) && <Box component="img" src={crest.get(team)} alt="" sx={{ width: 20, height: 20, objectFit: "contain", mr: 1.25 }} />}
            {clubInfo(team).name}
          </li>
        );
      }}
      renderInput={(p) => <TextField {...p} size="small" label={label} />}
      sx={darkField({ flex: 1, minWidth: { xs: "100%", sm: 220 } })}
    />
  );
  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        if (x && y) navigate(h2hPath(x, y));
      }}
      sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, maxWidth: 820, p: 1.5, borderRadius: 3, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
    >
      {field("h2h-club-a", "First club", x, setX, y)}
      <Typography sx={{ color: BRAND.textSecondary, fontWeight: 800 }}>v</Typography>
      {field("h2h-club-b", "Second club", y, setY, x)}
      <Button
        type="submit"
        variant="contained"
        disabled={!x || !y}
        sx={{ background: BRAND.gradient, fontWeight: 800, textTransform: "none", borderRadius: 999, px: 3, "&.Mui-disabled": { background: "rgba(255,255,255,0.08)", color: BRAND.textSecondary } }}
      >
        Compare
      </Button>
    </Box>
  );
}

function PairCard({ p, label, crest }) {
  const [x, y, n, xw, d, yw] = p;
  const img = (team) =>
    crest.get(team) ? <Box component="img" src={crest.get(team)} alt="" loading="lazy" sx={{ width: 30, height: 30, objectFit: "contain" }} /> : null;
  return (
    <Box
      component={RouterLink}
      to={h2hPath(x, y)}
      sx={{
        display: "block",
        height: "100%",
        boxSizing: "border-box",
        p: 1.75,
        borderRadius: 2,
        textDecoration: "none",
        color: "#fff",
        background: "linear-gradient(180deg, rgba(25,25,32,0.85) 0%, rgba(18,18,24,0.85) 100%)",
        border: "1px solid rgba(255,255,255,0.08)",
        transition: "border-color 140ms ease-out, transform 140ms ease-out",
        "&:hover, &:focus-visible": { borderColor: BRAND.cyan, transform: "translateY(-1px)", outline: "none" },
      }}
    >
      <Typography sx={{ color: BRAND.cyan, fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase" }}>{label}</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
        {img(x)}
        <Typography sx={{ fontWeight: 800, fontSize: 15 }}>
          {clubInfo(x).short} v {clubInfo(y).short}
        </Typography>
        <Box sx={{ ml: "auto" }}>{img(y)}</Box>
      </Box>
      <Typography sx={{ color: "#c9ccd6", fontSize: 13, mt: 1, fontFeatureSettings: '"tnum"' }}>
        {clubInfo(x).short} {xw} · {d} draw{d === 1 ? "" : "s"} · {clubInfo(y).short} {yw}
      </Typography>
      <Typography sx={{ color: BRAND.textSecondary, fontSize: 12.5 }}>{n} meetings</Typography>
    </Box>
  );
}

// This season's clubs against each other: meeting counts, shaded by how
// often, each square linking to the pair's page.
function Matrix({ clubs, pairOf, crest }) {
  const max = Math.max(1, ...clubs.flatMap((x) => clubs.map((y) => (x === y ? 0 : pairOf(x, y)?.[2] || 0))));
  return (
    <Box sx={{ overflowX: "auto", mb: 3 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `minmax(110px, auto) repeat(${clubs.length}, minmax(30px, 1fr))`,
          gap: "3px",
          minWidth: 110 + clubs.length * 33,
          maxWidth: 1100,
          "& .m": { aspectRatio: "1 / 1", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "5px", fontSize: 11.5, fontWeight: 800, fontFeatureSettings: '"tnum"', textDecoration: "none" },
          "& a.m": { color: "#0a0a0e" },
          "& a.m:hover, & a.m:focus-visible": { outline: `2px solid ${BRAND.cyan}`, outlineOffset: "1px" },
          "& .none": { background: "rgba(255,255,255,0.045)" },
          "& .self": { background: "transparent" },
        }}
      >
        <div />
        {clubs.map((c) => (
          <Box key={c} title={clubInfo(c).name} sx={{ display: "flex", justifyContent: "center", alignItems: "flex-end", pb: 0.5 }}>
            {crest.get(c) && <Box component="img" src={crest.get(c)} alt={clubInfo(c).short} loading="lazy" sx={{ width: 22, height: 22, objectFit: "contain" }} />}
          </Box>
        ))}
        {clubs.map((x) => (
          <React.Fragment key={x}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "#d7dae3", fontSize: 12.5, fontWeight: 700, whiteSpace: "nowrap", pr: 0.5 }}>
              {crest.get(x) && <Box component="img" src={crest.get(x)} alt="" loading="lazy" sx={{ width: 18, height: 18, objectFit: "contain" }} />}
              {clubInfo(x).short}
            </Box>
            {clubs.map((y) => {
              if (x === y) return <div key={y} className="m self" />;
              const p = pairOf(x, y);
              if (!p) return <div key={y} className="m none" title={`${clubInfo(x).short} and ${clubInfo(y).short} have never met`} />;
              const t = p[2] / max;
              return (
                <RouterLink
                  key={y}
                  to={h2hPath(x, y)}
                  className="m"
                  title={`${clubInfo(x).short} v ${clubInfo(y).short}: ${p[2]} meetings`}
                  aria-label={`${clubInfo(x).name} v ${clubInfo(y).name}: ${p[2]} meetings`}
                  style={{ background: `rgba(57, 208, 255, ${0.25 + 0.75 * t})` }}
                >
                  {p[2]}
                </RouterLink>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}
